import React, { useState } from 'react'
import { useMutation, gql } from '@apollo/client'
import { savePaymentGateways } from '../../../apollo'
import { Box, Typography, Switch, Alert, CircularProgress, Tooltip } from '@mui/material'
import TuneIcon from '@mui/icons-material/Tune'

const SAVE_PAYMENT_GATEWAYS = gql`${savePaymentGateways}`

const GATEWAYS = [
  {
    key: 'qor',
    name: 'QorCommerce',
    detail: 'Checkout option "Credit / Debit Card" with saved cards, and QorCommerce Black Card sign-ups.'
  },
  {
    key: 'stripe',
    name: 'Stripe',
    detail: 'Checkout option "Credit / Debit Card (Stripe)", and Stripe Black Card sign-ups.'
  }
]

/**
 * Which card gateways take NEW payments on the website and customer app:
 * order checkout and new Black Card memberships. Saves on flip. The last one
 * on can't be turned off, so customers can always pay by card. Existing Black
 * Card renewals and refunds on earlier orders are not affected.
 */
function PaymentGateways(props) {
  const [on, setOn] = useState({ qor: props.qorPaymentsEnabled !== false, stripe: props.stripePaymentsEnabled !== false })
  const [error, setError] = useState('')
  const [saved, setSaved] = useState('')

  const [save, { loading }] = useMutation(SAVE_PAYMENT_GATEWAYS, {
    onCompleted: (d) => {
      const c = d?.savePaymentGateways
      if (c) setOn({ qor: c.qorPaymentsEnabled !== false, stripe: c.stripePaymentsEnabled !== false })
      setSaved('Saved. Customers see the change on their next checkout.')
      setTimeout(() => setSaved(''), 3500)
    },
    onError: (e) => {
      setOn({ qor: props.qorPaymentsEnabled !== false, stripe: props.stripePaymentsEnabled !== false })
      setError(e.message || 'Failed to save.')
      setTimeout(() => setError(''), 4000)
    }
  })

  const flip = (key, value) => {
    const next = { ...on, [key]: value }
    if (!next.qor && !next.stripe) return
    setOn(next)
    save({ variables: { qor: next.qor, stripe: next.stripe } })
  }

  return (
    <Box sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
      <Box sx={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #0e7490 100%)', px: 2.5, py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <TuneIcon sx={{ color: '#a5f3fc', fontSize: 18 }} />
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#fff', flex: 1 }}>Card payment gateways</Typography>
        {loading && <CircularProgress size={14} sx={{ color: '#fff' }} />}
      </Box>

      <Box sx={{ p: 2.5, bgcolor: '#fff' }}>
        <Typography sx={{ fontSize: '0.78rem', color: '#6b7280', mb: 1.5 }}>
          Choose which gateways customers can pay with at checkout and when starting Black Card. At least one stays on.
          Existing Black Card renewals and refunds are not affected.
        </Typography>

        {GATEWAYS.map((g) => {
          const isLastOn = on[g.key] && Object.values(on).filter(Boolean).length === 1
          return (
            <Box key={g.key} sx={{ p: 1.5, mb: 1.25, borderRadius: '10px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: '#111827' }}>
                  {g.name}
                  <Typography component="span" sx={{ ml: 1, fontSize: '0.72rem', fontWeight: 700, color: on[g.key] ? '#047857' : '#9ca3af' }}>
                    {on[g.key] ? 'ON' : 'OFF'}
                  </Typography>
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', mt: 0.25 }}>{g.detail}</Typography>
              </Box>
              <Tooltip title={isLastOn ? 'Turn the other gateway on first. At least one must stay on.' : ''}>
                <span>
                  <Switch
                    checked={on[g.key]}
                    disabled={loading || isLastOn}
                    onChange={(e) => flip(g.key, e.target.checked)}
                    inputProps={{ 'aria-label': `${g.name} payments` }}
                  />
                </span>
              </Tooltip>
            </Box>
          )
        })}

        {saved && <Alert severity="success" sx={{ mt: 1, borderRadius: '10px', fontSize: '0.82rem' }}>{saved}</Alert>}
        {error && <Alert severity="error" sx={{ mt: 1, borderRadius: '10px', fontSize: '0.82rem' }}>{error}</Alert>}
      </Box>
    </Box>
  )
}

export default PaymentGateways
